/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useTheme, useMediaQuery } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import api from "api/api";
import Swal from "sweetalert2";

// 직책 코드 → 라벨
const POSITION_LABELS = {
  0: "대표",
  1: "팀장",
  2: "파트장",
  3: "매니저",
  // 4: "매니저",
  // 5: "매니저",
  // 6: "매니저",
  // 7: "매니저",
  8: "영양사",
};

// 부서 코드 → 라벨
const DEPT_LABELS = {
  0: "대표",
  // 1: "신사업팀",
  2: "회계팀",
  3: "인사팀",
  4: "영업팀",
  5: "운영팀",
  6: "개발팀",
  7: "현장",
  8: "급식사업부",
  9: "기획팀",
};

const HQ_DEPARTMENT_OPTIONS = Object.entries(DEPT_LABELS)
  .filter(([code]) => !["0", "7"].includes(String(code)))
  .map(([value, label]) => ({ value: String(value), label }));

const POSITION_OPTIONS = [
  { value: "1", label: "팀장" },
  { value: "2", label: "파트장" },
  { value: "3", label: "매니저" },
  // { value: "4", label: "매니저" },
  // { value: "5", label: "매니저" },
  // { value: "6", label: "매니저" },
  // { value: "7", label: "매니저" },
  { value: "8", label: "영양사" },
];

const HQ_POSITION_OPTIONS = POSITION_OPTIONS.filter((option) =>
  ["1", "2", "3"].includes(option.value)
);
const ACCOUNT_POSITION_OPTIONS = POSITION_OPTIONS.filter((option) => option.value === "8");

// 통합/유틸 인력 전용 직책(position_type) 옵션
const UTIL_MEMBER_TYPE_OPTIONS = [
  { value: "7", label: "통합" },
  { value: "6", label: "유틸" },
];

const PROFILE_TRACK_FIELDS = [
  "user_name",
  "password",
  "department",
  "account_id",
  "position",
  "join_dt",
  "birth_date",
  "phone",
  "address",
  "address_detail",
  "user_type",
  "util_member_type",
];

const asText = (value) => (value == null ? "" : String(value));

const normalizeCode = (value) => {
  const next = asText(value).trim();
  return next ? next : "";
};

const buildAddressFull = (address, addressDetail) => {
  const base = asText(address).trim();
  const detail = asText(addressDetail).trim();
  if (!base && !detail) return "-";
  return `${base}${detail ? ` ${detail}` : ""}`.trim();
};

const inferUserType = (row) => {
  if (normalizeCode(row.user_type)) return normalizeCode(row.user_type);
  if (asText(row.account_id).trim()) return "3";
  if (normalizeCode(row.position) === "0") return "1";
  return "2";
};

const toComparableValue = (field, value) => {
  if (["department", "account_id", "position", "user_type", "util_member_type"].includes(field)) {
    return normalizeCode(value);
  }
  return asText(value);
};

const getUtilMemberLabel = (positionType) => {
  const normalized = normalizeCode(positionType);
  if (normalized === "7") return "통합";
  if (normalized === "6") return "유틸";
  return "-";
};

const isCeo = (row) => normalizeCode(row.position) === "0";
const isHeadOffice = (row) => !asText(row.account_id).trim();

// accountissuesheet2 테이블 톤과 맞추기 위한 컬럼 설정
const TABLE_COLUMNS = [
  { key: "ui_index", label: "순번", width: 52, align: "center" },
  { key: "user_name", label: "성명", width: 78, align: "center" },
  { key: "user_id", label: "아이디", width: 96, align: "center" },
  { key: "password", label: "비밀번호", width: 92, align: "center" },
  { key: "dept_or_account", label: "부서/거래처", width: 190, align: "center" },
  { key: "position_label", label: "직책", width: 86, align: "center" },
  { key: "join_dt", label: "입사일자", width: 88, align: "center" },
  { key: "birth_date", label: "생년월일", width: 88, align: "center" },
  { key: "phone", label: "전화번호", width: 104, align: "center" },
  { key: "address_full", label: "주소", width: 256, align: "left" },
  { key: "del_yn", label: "퇴사여부", width: 72, align: "center" },
];

// accountissuesheet2 본문과 동일 계열 폰트/크기
const TABLE_FONT_FAMILY = "inherit";
const TABLE_FONT_SIZE = 12;
const CELL_HEIGHT = 33;
const CELL_INNER_HEIGHT = 23;
const MIN_LOADING_MODAL_MS = 420;
const PAGE_SIZE = 19;
// 저장 전 변경 셀 강조 색상(accountissuesheettab과 동일)
const CHANGED_ACCENT_COLOR = "#d32f2f";
// 신규 본사 사용자 행 식별 접두어
const NEW_HQ_USER_ROW_PREFIX = "__new_hq_user__";
// 신규 본사 사용자 필수 입력 항목
const REQUIRED_NEW_HQ_USER_FIELDS = [
  { field: "user_name", label: "성명" },
  { field: "user_id", label: "아이디" },
  { field: "password", label: "비밀번호" },
  { field: "department", label: "부서" },
  { field: "position", label: "직책" },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const toNullableNumber = (value) => {
  const normalized = normalizeCode(value);
  return normalized === "" ? null : Number(normalized);
};

const toNullableDate = (value) => {
  const next = asText(value).trim();
  return next ? next : null;
};

const parseApiCode = (data) => {
  if (data && typeof data === "object" && data.code != null) {
    return String(data.code);
  }

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (parsed && parsed.code != null) return String(parsed.code);
    } catch (e) {
      return "";
    }
  }

  return "";
};

const buildUserSavePayload = (row) => {
  const userTypeCode = normalizeCode(row.user_type) || inferUserType(row);
  const accountId = asText(row.account_id).trim();
  const normalizedDepartment = userTypeCode === "4" ? 7 : toNullableNumber(row.department);
  const utilMemberType = normalizeCode(row.util_member_type);
  const payload = {
    info: {
      user_id: asText(row.user_id).trim(),
      user_name: asText(row.user_name).trim(),
      password: asText(row.password),
      user_type: toNullableNumber(userTypeCode),
      join_dt: toNullableDate(row.join_dt),
      department: normalizedDepartment,
      position: userTypeCode === "4" ? null : toNullableNumber(row.position),
      account_id: accountId || null,
      util_member_type: userTypeCode === "4" ? utilMemberType || null : null,
      use_yn: row.is_new_hq_user ? "Y" : normalizeCode(row.use_yn) || null,
    },
    detail: {
      user_id: asText(row.user_id).trim(),
      phone: asText(row.phone).trim() || null,
      address: asText(row.address).trim() || (row.is_new_hq_user ? null : ""),
      address_detail: asText(row.address_detail).trim() || (row.is_new_hq_user ? null : ""),
      zipcode: asText(row.zipcode).trim() || null,
      birth_date: toNullableDate(row.birth_date),
    },
  };

  if (!row.is_new_hq_user) {
    payload.is_update = true;
  }

  // 원래 /User/UserRgt 저장 로직을 유지하면서 통합/유틸(account_members) 생성까지 동일 경로로 태움
  if (userTypeCode === "4" && (utilMemberType === "6" || utilMemberType === "7")) {
    payload.account_member = {
      position_type: Number(utilMemberType), // 7: 통합, 6: 유틸
    };
  }

  return payload;
};

const readSingleRow = (data) => {
  if (Array.isArray(data)) return data[0] || null;
  if (data && typeof data === "object") return data;
  return null;
};

const collectProfileChangedRows = (targetRows, originalsById) =>
  targetRows.filter((row) => {
    if (row.is_new_hq_user) return true;

    const original = originalsById[row.user_id];
    if (!original) return false;

    return PROFILE_TRACK_FIELDS.some(
      (field) => toComparableValue(field, row[field]) !== toComparableValue(field, original[field])
    );
  });

const collectDelEntriesFromRows = (targetRows, originalsById) =>
  targetRows
    .map((row) => {
      const original = originalsById[row.user_id] || {};
      const nextDelYn = asText(row.del_yn || "N").toUpperCase();
      const originalDelYn = asText(original.orig_del_yn ?? original.del_yn ?? "N").toUpperCase();
      if (nextDelYn === originalDelYn) return null;

      return {
        user_id: row.user_id,
        del_yn: nextDelYn,
      };
    })
    .filter(Boolean);

const buildSearchBlob = (row) => {
  const isField =
    asText(row.account_id).trim() || normalizeCode(row.user_type) === "4" ? "현장" : "본사";

  return [
    row.ui_index,
    row.user_id,
    row.user_name,
    row.dept_or_account,
    row.position_label,
    row.join_dt,
    row.birth_date,
    row.phone,
    row.address_full,
    row.del_yn === "Y" ? "퇴사" : "재직",
    isField,
  ]
    .map((value) => asText(value).toLowerCase())
    .join(" ");
};

function UserManagement({ embedded = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileTablet = useMediaQuery("(max-width:1279.95px)");

  // 화면 데이터/상태
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingDelYn, setPendingDelYn] = useState({});
  const [pendingProfileChanges, setPendingProfileChanges] = useState({});
  const [originalRowsById, setOriginalRowsById] = useState({});
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountOptionsLoading, setAccountOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 입력창 값과 실제 필터 적용 값을 분리해서, Enter 입력 시에만 검색이 실행되게 함
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState("");
  const [editingAccountUserId, setEditingAccountUserId] = useState("");
  const [invalidRequiredCells, setInvalidRequiredCells] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInputResetKey, setSearchInputResetKey] = useState(0);
  const searchKeywordDraftRef = useRef("");
  const pendingFocusCellRef = useRef(null);
  const rowsRef = useRef(rows);
  const originalRowsByIdRef = useRef(originalRowsById);

  const accountOptionById = useMemo(() => {
    const map = new Map();
    accountOptions.forEach((option) => {
      map.set(String(option.account_id), option);
    });
    return map;
  }, [accountOptions]);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    originalRowsByIdRef.current = originalRowsById;
  }, [originalRowsById]);

  // 서버 응답을 화면 전용 행 데이터로 정규화
  const mapUserRow = (item, index) => {
    const department = normalizeCode(item.department);
    const account_id = asText(item.account_id).trim();
    const account_name = asText(item.account_name);
    const position = normalizeCode(item.position);
    const user_type = normalizeCode(item.user_type);
    const util_member_type_raw = normalizeCode(item.util_member_type);
    const util_member_type =
      util_member_type_raw || (position === "6" || position === "7" ? position : "");
    const normalizedUserType =
      user_type || (util_member_type ? "4" : account_id ? "3" : position === "0" ? "1" : "2");
    const del_yn = asText(item.del_yn || "N").toUpperCase();
    const positionLabel =
      normalizedUserType === "4"
        ? getUtilMemberLabel(util_member_type)
        : POSITION_LABELS[position] ?? position ?? "-";

    const mappedRow = {
      row_key: asText(item.user_id),
      ui_index: index + 1,
      user_id: asText(item.user_id),
      user_name: asText(item.user_name),
      password: asText(item.password),
      department,
      account_id,
      account_name,
      position,
      util_member_type,
      user_type: normalizedUserType,
      position_label: positionLabel,
      join_dt: asText(item.join_dt),
      birth_date: asText(item.birth_date),
      phone: asText(item.phone),
      address: asText(item.address),
      address_detail: asText(item.address_detail),
      zipcode: asText(item.zipcode),
      del_yn,
      orig_del_yn: del_yn,
      dept_or_account:
        account_name || (account_id ? account_id : DEPT_LABELS[department] ?? department ?? "-"),
      address_full: buildAddressFull(item.address, item.address_detail),
    };

    mappedRow.search_blob = buildSearchBlob(mappedRow);
    return mappedRow;
  };

  // 사용자 목록 조회 (서버 → 화면 상태)
  const fetchUsers = async () => {
    if (loading) return;

    let loadingOpenedAt = 0;
    try {
      setLoading(true);
      Swal.fire({
        title: "불러오는 중...",
        text: "사용자 목록을 조회하고 있습니다.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      loadingOpenedAt = Date.now();

      const res = await api.get("/User/UserManageList");
      const list = Array.isArray(res.data) ? res.data : [];
      const mapped = list.map((item, idx) => mapUserRow(item, idx));
      const originals = {};
      mapped.forEach((row) => {
        originals[row.user_id] = { ...row };
      });

      setRows(mapped);
      rowsRef.current = mapped;
      setOriginalRowsById(originals);
      originalRowsByIdRef.current = originals;
      setPendingDelYn({});
      setPendingProfileChanges({});
      setEditingAccountUserId("");
      setCurrentPage(1);
    } catch (err) {
      console.error("사용자 목록 조회 실패:", err);
      Swal.fire("조회 실패", "사용자 목록을 불러오지 못했습니다.", "error");
      setRows([]);
      rowsRef.current = [];
    } finally {
      setLoading(false);
      if (loadingOpenedAt > 0) {
        const elapsed = Date.now() - loadingOpenedAt;
        const remain = Math.max(MIN_LOADING_MODAL_MS - elapsed, 0);
        if (remain > 0) {
          await wait(remain);
        }
      }
      const currentTitle = Swal.getTitle()?.textContent || "";
      if (Swal.isVisible() && currentTitle.includes("불러오는 중")) {
        Swal.close();
      }
    }
  };

  // 거래처 검색용 옵션 조회
  const fetchAccountOptions = async () => {
    if (accountOptions.length > 0 || accountOptionsLoading) return;

    try {
      setAccountOptionsLoading(true);
      const res = await api.get("/Account/AccountList", {
        params: { account_type: 0 },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setAccountOptions(
        list
          .map((item) => ({
            account_id: asText(item.account_id),
            account_name: asText(item.name || item.account_name || item.account_id),
          }))
          .filter((item) => item.account_id)
      );
    } catch (err) {
      console.error("거래처 목록 조회 실패:", err);
      setAccountOptions([]);
    } finally {
      setAccountOptionsLoading(false);
    }
  };

  const fetchUserInfoForSave = async (userId) => {
    try {
      const res = await api.get("/User/SelectUserInfo", { params: { user_id: userId } });
      return readSingleRow(res.data);
    } catch (err) {
      console.error(`저장 전 사용자 상세 조회 실패 (${userId}):`, err);
      return null;
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Enter 입력 시 현재 검색어를 필터 키로 확정하고 1페이지로 이동
  const applySearch = () => {
    setAppliedSearchKeyword(searchKeywordDraftRef.current);
    setCurrentPage(1);
  };

  // 새로고침 시 검색어/필터를 초기화하고 전체 목록을 다시 조회
  const handleRefreshUsers = () => {
    searchKeywordDraftRef.current = "";
    setAppliedSearchKeyword("");
    setInvalidRequiredCells({});
    setSearchInputResetKey((prev) => prev + 1);
    fetchUsers();
  };

  // 본사 사용자 신규 행 생성
  const handleAddHeadOfficeUser = () => {
    const rowKey = `${NEW_HQ_USER_ROW_PREFIX}${Date.now()}`;
    const newRow = {
      row_key: rowKey,
      is_new_hq_user: true,
      ui_index: "",
      user_id: "",
      user_name: "",
      password: "",
      department: "",
      account_id: "",
      account_name: "",
      position: "",
      util_member_type: "",
      user_type: "2",
      position_label: "-",
      join_dt: "",
      birth_date: "",
      phone: "",
      address: "",
      address_detail: "",
      zipcode: "",
      del_yn: "N",
      orig_del_yn: "N",
      use_yn: "Y",
      dept_or_account: "-",
      address_full: "-",
    };

    newRow.search_blob = buildSearchBlob(newRow);
    pendingFocusCellRef.current = { rowKey, field: "user_name" };
    searchKeywordDraftRef.current = "";
    setAppliedSearchKeyword("");
    setSearchInputResetKey((prev) => prev + 1);
    setCurrentPage(1);
    setRows((prev) => {
      const nextRows = [newRow, ...prev];
      rowsRef.current = nextRows;
      return nextRows;
    });
  };

  // 신규 본사 사용자 필수값 검증
  const validateNewHeadOfficeUsers = (targetRows) => {
    const nextInvalidCells = {};

    targetRows
      .filter((row) => row.is_new_hq_user)
      .forEach((row) => {
        const rowKey = getRowKey(row);
        const invalidFields = REQUIRED_NEW_HQ_USER_FIELDS.filter(({ field }) => {
          if (field === "department" || field === "position") return !normalizeCode(row[field]);
          return !asText(row[field]).trim();
        });

        if (invalidFields.length > 0) {
          nextInvalidCells[rowKey] = invalidFields.map(({ field }) => field);
        }
      });

    setInvalidRequiredCells(nextInvalidCells);
    return Object.keys(nextInvalidCells).length > 0;
  };

  // accountissuesheet2와 동일한 테이블 외곽/헤더/본문 톤
  const tableSx = {
    flex: 1,
    maxHeight: "79vh",
    overflow: "auto",
    "& table": {
      width: "max-content",
      minWidth: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      tableLayout: "fixed",
    },
    "& th, & td": {
      border: "1px solid #cfd6de",
      textAlign: "center",
      padding: "0 4px",
      fontSize: `${TABLE_FONT_SIZE}px`,
      fontFamily: TABLE_FONT_FAMILY,
      fontWeight: 400,
      color: "#111",
      background: "#fff",
      whiteSpace: "nowrap",
      lineHeight: 1.2,
      height: `${CELL_HEIGHT}px`,
      boxSizing: "border-box",
      verticalAlign: "middle",
    },
    "& thead th": {
      position: "sticky",
      top: -1,
      background: "#dbe7f5 !important",
      zIndex: 8,
      fontWeight: 700,
      fontFamily: TABLE_FONT_FAMILY,
      color: "#344054",
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: `${CELL_HEIGHT}px`,
      verticalAlign: "middle",
      boxShadow: "none",
      borderBottom: "1px solid #cfd6de",
      transform: "translateZ(0)",
      backgroundClip: "padding-box",
    },
  };

  const seqColumnWidth = TABLE_COLUMNS.find((col) => col.key === "ui_index")?.width ?? 52;

  // 모바일/태블릿에서는 순번/성명만 좌측 고정하고 나머지 컬럼은 가로 스크롤로 이동한다.
  const getStickyColumnStyle = (columnKey, isHeader = false) => {
    if (!isMobileTablet) return {};

    if (columnKey === "ui_index") {
      return {
        position: "sticky",
        left: 0,
        zIndex: isHeader ? 12 : 6,
        background: isHeader ? "#dbe7f5" : "#fff",
      };
    }

    if (columnKey === "user_name") {
      return {
        position: "sticky",
        left: seqColumnWidth,
        zIndex: isHeader ? 11 : 5,
        background: isHeader ? "#dbe7f5" : "#fff",
        boxShadow: "2px 0 0 #cfd6de",
      };
    }

    return {};
  };

  // 드롭다운 화살표/텍스트 간격을 모든 셀에서 동일하게 강제
  const compactSelectSx = {
    minWidth: 72,
    height: CELL_INNER_HEIGHT,
    margin: 0,
    color: "#111",
    backgroundColor: "#f3f5f7",
    borderRadius: "4px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "& .MuiSelect-select, & .MuiSelect-select.MuiSelect-select": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
      paddingLeft: "8px !important",
      paddingRight: "8px !important",
      fontSize: TABLE_FONT_SIZE,
      fontFamily: TABLE_FONT_FAMILY,
      fontWeight: 400,
      minHeight: `${CELL_INNER_HEIGHT}px !important`,
      lineHeight: `${CELL_INNER_HEIGHT}px !important`,
      display: "flex !important",
      alignItems: "center !important",
      justifyContent: "center",
      textAlign: "center",
      boxSizing: "border-box",
      height: `${CELL_INNER_HEIGHT}px !important`,
    },
    "&.MuiInputBase-root, & .MuiInputBase-root": {
      minHeight: `${CELL_INNER_HEIGHT}px`,
      height: `${CELL_INNER_HEIGHT}px`,
      alignItems: "center !important",
      margin: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiInputBase-input": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: 0,
    },
    "& .MuiSelect-icon": {
      display: "none",
    },
  };

  // 행 변경 시 원본 대비 diff를 계산해 "화면 저장 대상"으로 적재
  const getRowKey = (row) => asText(row.row_key || row.user_id);

  const syncPendingProfileByRow = (userId, nextRow) => {
    if (nextRow.is_new_hq_user) return;

    const original = originalRowsById[userId];
    if (!original) return;

    const changed = {};
    PROFILE_TRACK_FIELDS.forEach((field) => {
      const beforeValue = toComparableValue(field, original[field]);
      const afterValue = toComparableValue(field, nextRow[field]);
      if (beforeValue !== afterValue) {
        changed[field] = nextRow[field];
      }
    });

    setPendingProfileChanges((prev) => {
      const next = { ...prev };
      if (Object.keys(changed).length === 0) {
        delete next[userId];
      } else {
        next[userId] = changed;
      }
      return next;
    });
  };

  // 특정 행에 patch를 적용하고 표시용 파생값(직책 라벨/주소 등)도 함께 갱신
  const applyRowPatch = (rowKey, patch) => {
    let updatedRow = null;

    setRows((prev) => {
      const nextRows = prev.map((row) => {
        if (getRowKey(row) !== rowKey) return row;

        const next = { ...row, ...patch };
        const normalized = {
          ...next,
          department: normalizeCode(next.department),
          account_id: asText(next.account_id).trim(),
          account_name: asText(next.account_name),
          position: normalizeCode(next.position),
          util_member_type: normalizeCode(next.util_member_type),
          user_type: inferUserType(next),
          user_id: asText(next.user_id),
          user_name: asText(next.user_name),
          password: asText(next.password),
          join_dt: asText(next.join_dt),
          birth_date: asText(next.birth_date),
          phone: asText(next.phone),
          address: asText(next.address),
          address_detail: asText(next.address_detail),
          zipcode: asText(next.zipcode),
        };

        normalized.position_label =
          normalized.user_type === "4"
            ? getUtilMemberLabel(normalized.util_member_type)
            : POSITION_LABELS[normalized.position] ?? normalized.position ?? "-";
        normalized.dept_or_account =
          normalized.account_name ||
          (normalized.account_id
            ? normalized.account_id
            : DEPT_LABELS[normalized.department] ?? normalized.department ?? "-");
        normalized.address_full = buildAddressFull(normalized.address, normalized.address_detail);
        normalized.search_blob = buildSearchBlob(normalized);

        updatedRow = normalized;
        return normalized;
      });
      rowsRef.current = nextRows;
      return nextRows;
    });

    if (updatedRow) {
      syncPendingProfileByRow(rowKey, updatedRow);
    }
  };

  // 텍스트 셀 변경
  const handleTextFieldChange = (rowKey, field, value) => {
    applyRowPatch(rowKey, { [field]: value });
    setInvalidRequiredCells((prev) => {
      if (!prev[rowKey]?.includes(field) || !asText(value).trim()) return prev;

      const nextFields = prev[rowKey].filter((item) => item !== field);
      const next = { ...prev };
      if (nextFields.length > 0) {
        next[rowKey] = nextFields;
      } else {
        delete next[rowKey];
      }
      return next;
    });
  };

  const clearInvalidRequiredCell = (rowKey, field) => {
    setInvalidRequiredCells((prev) => {
      if (!prev[rowKey]?.includes(field)) return prev;

      const nextFields = prev[rowKey].filter((item) => item !== field);
      const next = { ...prev };
      if (nextFields.length > 0) {
        next[rowKey] = nextFields;
      } else {
        delete next[rowKey];
      }
      return next;
    });
  };

  // 본사 사용자의 부서 변경
  const handleDepartmentChange = (userId, nextDepartment) => {
    applyRowPatch(userId, {
      department: normalizeCode(nextDepartment),
      account_id: "",
      account_name: "",
      user_type: "2",
    });
    clearInvalidRequiredCell(userId, "department");
  };

  // 거래처 사용자의 거래처 변경
  const handleAccountChange = (userId, option) => {
    if (!option) return;
    applyRowPatch(userId, {
      account_id: asText(option.account_id),
      account_name: asText(option.account_name),
      department: "7",
      user_type: "3",
    });
  };

  // 직책 변경
  const handlePositionChange = (userId, nextPosition) => {
    applyRowPatch(userId, { position: normalizeCode(nextPosition) });
    clearInvalidRequiredCell(userId, "position");
  };

  // 통합/유틸 직책(position_type) 변경
  const handleUtilMemberTypeChange = (userId, nextType) => {
    const normalizedType = normalizeCode(nextType);
    applyRowPatch(userId, {
      user_type: "4",
      department: "7",
      util_member_type: normalizedType,
      position: "",
      account_id: "",
      account_name: "",
    });
  };

  // 퇴사여부 변경
  const handleDelYnChange = (userId, nextValue) => {
    const nextUpper = asText(nextValue).toUpperCase();
    setRows((prev) => {
      const nextRows = prev.map((row) => {
        if (row.user_id !== userId) return row;
        const nextRow = { ...row, del_yn: nextUpper };
        nextRow.search_blob = buildSearchBlob(nextRow);
        return nextRow;
      });
      rowsRef.current = nextRows;
      return nextRows;
    });

    const originalValue = asText(originalRowsById[userId]?.orig_del_yn || "N").toUpperCase();
    setPendingDelYn((prev) => {
      const next = { ...prev };
      if (nextUpper === originalValue) {
        delete next[userId];
      } else {
        next[userId] = nextUpper;
      }
      return next;
    });
  };

  // 드롭다운이 있든 없든 동일 높이로 보이게 맞추는 본문 텍스트 기본 스타일
  const cellTextStyle = {
    fontFamily: TABLE_FONT_FAMILY,
    fontSize: `${TABLE_FONT_SIZE}px`,
    fontWeight: 400,
    lineHeight: "1.2",
    display: "inline-flex",
    alignItems: "center",
    minHeight: `${CELL_INNER_HEIGHT}px`,
  };

  const controlCellWrapStyle = {
    width: "100%",
    minHeight: `${CELL_INNER_HEIGHT}px`,
    height: `${CELL_INNER_HEIGHT}px`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: 0,
    padding: 0,
    boxSizing: "border-box",
  };

  const makeCellTextStyle = (extra = {}) => ({
    ...cellTextStyle,
    ...extra,
  });

  // Select 계열은 변경된 경우 텍스트/테두리를 빨간색으로 표시
  const changedSelectSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "& .MuiSelect-select": {
      color: CHANGED_ACCENT_COLOR,
    },
    "& .MuiSelect-select.MuiSelect-select": {
      color: CHANGED_ACCENT_COLOR,
    },
  };

  // Autocomplete 계열은 변경된 경우 입력 텍스트/테두리를 빨간색으로 표시
  const changedAutocompleteSx = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${CHANGED_ACCENT_COLOR} !important`,
    },
    "& .MuiInputBase-input": {
      color: CHANGED_ACCENT_COLOR,
    },
  };

  // 원본 대비 셀 변경 여부 계산(렌더 표시 전용)
  const isTrackFieldChanged = (row, field) => {
    if (row.is_new_hq_user) {
      if (field === "user_type") return false;
      if (["department", "account_id", "position", "user_type", "util_member_type"].includes(field)) {
        return !!normalizeCode(row[field]);
      }
      return !!asText(row[field]).trim();
    }

    const original = originalRowsById[row.user_id];
    if (!original) return false;

    return toComparableValue(field, row[field]) !== toComparableValue(field, original[field]);
  };

  const isDelYnChanged = (row) => {
    const original = originalRowsById[row.user_id];
    if (!original) return false;

    const nextDelYn = asText(row.del_yn || "N").toUpperCase();
    const originalDelYn = asText(original.orig_del_yn ?? original.del_yn ?? "N").toUpperCase();
    return nextDelYn !== originalDelYn;
  };

  // 부서/거래처 셀은 user_type, department, account_id 변경을 함께 본다
  const isDeptOrAccountChanged = (row) =>
    isTrackFieldChanged(row, "user_type") ||
    isTrackFieldChanged(row, "department") ||
    isTrackFieldChanged(row, "account_id");

  // 직책 셀은 user_type, position, util_member_type 변경을 함께 본다
  const isPositionCellChanged = (row) =>
    isTrackFieldChanged(row, "user_type") ||
    isTrackFieldChanged(row, "position") ||
    isTrackFieldChanged(row, "util_member_type");

  // 컬럼별 변경 판단(렌더 표시 전용)
  const isCellChanged = (row, columnKey) => {
    if (!row) return false;

    switch (columnKey) {
      case "user_name":
      case "user_id":
      case "password":
      case "join_dt":
      case "birth_date":
      case "phone":
        return isTrackFieldChanged(row, columnKey);
      case "address_full":
        return isTrackFieldChanged(row, "address") || isTrackFieldChanged(row, "address_detail");
      case "dept_or_account":
        return isDeptOrAccountChanged(row);
      case "position_label":
        return isPositionCellChanged(row);
      case "del_yn":
        return isDelYnChanged(row);
      default:
        return false;
    }
  };

  // 아이디는 클릭 안내만 제공하고 편집은 막는다
  const handleReadonlyIdClick = () => {
    Swal.fire("안내", "아이디는 변경할 수 없습니다.", "info");
  };

  // contentEditable 입력 중에도 원본 대비 변경 여부를 즉시 색상으로 반영
  const syncEditableDraftColor = (element, userId, field) => {
    if (!element) return;
    const original = originalRowsById[userId];
    const draftValue = asText(element.value ?? element.textContent);
    const originalValue = asText(original?.[field]);
    element.style.color = draftValue !== originalValue ? CHANGED_ACCENT_COLOR : "#111";
  };

  // 텍스트를 클릭하면 같은 자리에서 바로 수정되도록 contentEditable을 사용한다
  const renderEditableCell = (row, field, align = "left", isChanged = false, padding = "1px 2px 0") => {
    const rowKey = getRowKey(row);
    const value = asText(row[field]);
    const isInvalid = invalidRequiredCells[rowKey]?.includes(field);

    return (
      <input
        key={`${rowKey}_${field}_${value}`}
        type="text"
        defaultValue={value}
        spellCheck={false}
        ref={(element) => {
          const pending = pendingFocusCellRef.current;
          if (!element || pending?.rowKey !== rowKey || pending?.field !== field) return;

          pendingFocusCellRef.current = null;
          setTimeout(() => {
            element.focus();
            element.setSelectionRange(element.value.length, element.value.length);
          }, 0);
        }}
        style={{
          fontFamily: TABLE_FONT_FAMILY,
          fontSize: `${TABLE_FONT_SIZE}px`,
          fontWeight: 400,
          color: isChanged ? CHANGED_ACCENT_COLOR : "#111",
          border: isInvalid ? `1px solid ${CHANGED_ACCENT_COLOR}` : "1px solid transparent",
          borderRadius: "4px",
          display: "block",
          textAlign: align,
          width: "100%",
          minHeight: `${CELL_INNER_HEIGHT}px`,
          height: `${CELL_INNER_HEIGHT}px`,
          lineHeight: "normal",
          margin: 0,
          padding,
          boxSizing: "border-box",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          outline: "none",
          cursor: "text",
          background: "transparent",
        }}
        onFocus={(e) => {
          syncEditableDraftColor(e.currentTarget, rowKey, field);
        }}
        onChange={(e) => {
          syncEditableDraftColor(e.currentTarget, rowKey, field);
        }}
        onBlur={(e) => {
          const nextValue = asText(e.currentTarget.value);
          syncEditableDraftColor(e.currentTarget, rowKey, field);
          if (nextValue !== value) {
            handleTextFieldChange(rowKey, field, nextValue);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === "Escape") {
            e.preventDefault();
            e.currentTarget.value = value;
            e.currentTarget.blur();
          }
        }}
      />
    );
  };

  // 변경된 사용자 정보/퇴사여부를 DB에 저장
  const handleSaveChanges = async () => {
    // contentEditable 마지막 입력이 onBlur로 반영되도록 저장 전에 포커스를 해제
    const activeEl = document.activeElement;
    if (activeEl && typeof activeEl.blur === "function") {
      activeEl.blur();
      await wait(0);
    }

    const snapshotRows = rowsRef.current;
    const snapshotOriginals = originalRowsByIdRef.current;
    const hasInvalidNewUser = validateNewHeadOfficeUsers(snapshotRows);
    if (hasInvalidNewUser) {
      Swal.fire("필수값 입력 필요", "빨간 테두리 항목은 필수값입니다.", "warning");
      return;
    }

    const profileRows = collectProfileChangedRows(snapshotRows, snapshotOriginals);
    const profileUserIds = profileRows.map((row) => row.user_id);
    const delEntries = collectDelEntriesFromRows(snapshotRows, snapshotOriginals);

    if (profileUserIds.length === 0 && delEntries.length === 0) {
      Swal.fire("알림", "변경 사항이 없습니다.", "info");
      return;
    }

    try {
      setSaving(true);
      Swal.fire({
        title: "저장 중...",
        text: "변경사항을 반영하고 있습니다.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });

      // 1) 사용자 기본정보 저장 (단건씩 backup 조회 후 배열로 일괄 전송)
      const backups = await Promise.all(
        profileRows.map((row) =>
          row.is_new_hq_user ? Promise.resolve(null) : fetchUserInfoForSave(row.user_id)
        )
      );

      const payloads = profileRows.map((row, i) => {
        const backup = backups[i];
        const mergedRow = {
          ...backup,
          ...row,
          user_type:
            normalizeCode(row.user_type) ||
            normalizeCode(backup?.user_type) ||
            inferUserType(row),
          util_member_type:
            normalizeCode(row.util_member_type) ||
            normalizeCode(backup?.util_member_type) ||
            (normalizeCode(row.position) === "6" || normalizeCode(row.position) === "7"
              ? normalizeCode(row.position)
              : normalizeCode(backup?.position)),
          zipcode: asText(row.zipcode).trim() || asText(backup?.zipcode).trim(),
        };

        if (
          normalizeCode(mergedRow.user_type) === "4" &&
          !["6", "7"].includes(normalizeCode(mergedRow.util_member_type))
        ) {
          throw new Error(`통합/유틸 구분값을 선택해주세요. (${row.user_id})`);
        }

        const payload = buildUserSavePayload(mergedRow);
        if (row.is_new_hq_user) {
          payload.info.use_yn = "Y";
        }
        return payload;
      });

      if (payloads.length > 0) {
        const res = await api.post("/User/UserRgt_v2", payloads);
        const code = parseApiCode(res.data);
        if (code && code !== "200") {
          throw new Error(`사용자 정보 저장 실패${res?.data?.message ? `: ${res.data.message}` : ""}`);
        }
      }

      // 2) 퇴사여부 저장
      for (const entry of delEntries) {
        const res = await api.post("/User/UserDelYnSave", entry);
        const code = parseApiCode(res.data);
        if (code && code !== "200") {
          throw new Error(
            `퇴사여부 저장 실패 (${entry.user_id})${res?.data?.msg ? `: ${res.data.msg}` : ""}`
          );
        }
      }

      const savedRows = snapshotRows.map((row, index) => ({
        ...row,
        row_key: asText(row.user_id),
        is_new_hq_user: false,
        ui_index: index + 1,
        orig_del_yn: asText(row.del_yn || "N").toUpperCase(),
      }));
      const nextOriginals = {};
      savedRows.forEach((row) => {
        nextOriginals[row.user_id] = { ...row };
      });
      setRows(savedRows);
      rowsRef.current = savedRows;
      setOriginalRowsById(nextOriginals);
      originalRowsByIdRef.current = nextOriginals;
      setPendingProfileChanges({});
      setPendingDelYn({});
      setInvalidRequiredCells({});

      const messages = [];
      if (profileUserIds.length > 0) messages.push(`사용자 정보 ${profileUserIds.length}건`);
      if (delEntries.length > 0) messages.push(`퇴사여부 ${delEntries.length}건`);

      if (Swal.isVisible()) Swal.close();
      Swal.fire("저장 완료", `${messages.join(", ")} DB에 저장되었습니다.`, "success");
    } catch (err) {
      if (Swal.isVisible()) Swal.close();
      console.error("사용자 관리 저장 실패:", err);
      Swal.fire("저장 실패", err.message || "저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  // 컬럼 key 기준으로 셀 내용을 렌더링하는 함수
  const renderTableCellContent = (row, columnKey) => {
    const cellChanged = isCellChanged(row, columnKey);

    // 1) 순번
    if (columnKey === "ui_index") {
      return (
        <span
          style={makeCellTextStyle({
            fontWeight: 500,
            color: "#555",
            width: "100%",
            justifyContent: "center",
          })}
        >
          {row.is_new_hq_user ? "" : row.ui_index}
        </span>
      );
    }

    // 2) 아이디(클릭은 가능하지만 변경 불가 안내만 표시)
    if (columnKey === "user_id") {
      if (row.is_new_hq_user) {
        return renderEditableCell(row, "user_id", "center", cellChanged);
      }

      return (
        <span
          role="button"
          tabIndex={0}
          style={makeCellTextStyle({
            color: "#111",
            cursor: "not-allowed",
            textDecoration: "underline dotted",
            textUnderlineOffset: "3px",
            width: "100%",
            justifyContent: "center",
          })}
          onClick={handleReadonlyIdClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleReadonlyIdClick();
          }}
        >
          {row.user_id || "-"}
        </span>
      );
    }

    // 3) 일반 텍스트 편집 셀
    if (columnKey === "user_name") return renderEditableCell(row, "user_name", "center", cellChanged, "0 2px 2px");
    if (columnKey === "password") return renderEditableCell(row, "password", "center", cellChanged);
    if (columnKey === "join_dt") return renderEditableCell(row, "join_dt", "center", cellChanged);
    if (columnKey === "birth_date") return renderEditableCell(row, "birth_date", "center", cellChanged);
    if (columnKey === "phone") return renderEditableCell(row, "phone", "center", cellChanged);
    if (columnKey === "address_full") return renderEditableCell(row, "address", "left", cellChanged, "0 2px 2px");

    // 4) 부서/거래처 클릭 편집 셀
    if (columnKey === "dept_or_account") {
      const deptOrAccountChanged = cellChanged;
      const rowKey = getRowKey(row);
      const deptRequiredInvalid = invalidRequiredCells[rowKey]?.includes("department");

      if (normalizeCode(row.user_type) === "4") {
        return (
          <div style={controlCellWrapStyle}>
            <Select
              size="small"
              value="현장"
              disabled
              sx={{
                ...compactSelectSx,
                ...(deptOrAccountChanged ? changedSelectSx : {}),
                minWidth: 112,
                width: "100%",
              }}
            >
              <MenuItem value="현장">현장</MenuItem>
            </Select>
          </div>
        );
      }

      if (isCeo(row)) {
        return (
          <div style={controlCellWrapStyle}>
            <Select
              size="small"
              value="대표"
              disabled
              sx={{
                ...compactSelectSx,
                ...(deptOrAccountChanged ? changedSelectSx : {}),
                minWidth: 112,
                width: "100%",
              }}
            >
              <MenuItem value="대표">대표</MenuItem>
            </Select>
          </div>
        );
      }

      if (isHeadOffice(row)) {
        return (
          <div style={controlCellWrapStyle}>
            <Select
              size="small"
              value={normalizeCode(row.department)}
              onChange={(e) => {
                handleDepartmentChange(getRowKey(row), e.target.value);
              }}
              sx={{
                ...compactSelectSx,
                ...(deptOrAccountChanged || deptRequiredInvalid ? changedSelectSx : {}),
                minWidth: 112,
                width: "100%",
              }}
            >
              {HQ_DEPARTMENT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }

      const selected =
        accountOptionById.get(asText(row.account_id)) ||
        (row.account_id
          ? {
            account_id: asText(row.account_id),
            account_name: asText(row.account_name || row.account_id),
          }
          : null);

      if (editingAccountUserId !== row.user_id) {
        return (
          <div style={controlCellWrapStyle}>
            <span
              role="button"
              tabIndex={0}
              style={{
                ...makeCellTextStyle({
                  width: "100%",
                  justifyContent: "center",
                  backgroundColor: "#f3f5f7",
                  borderRadius: "4px",
                  height: `${CELL_INNER_HEIGHT}px`,
                  cursor: "pointer",
                  color: deptOrAccountChanged ? CHANGED_ACCENT_COLOR : "#111",
                  border: deptOrAccountChanged
                    ? `1px solid ${CHANGED_ACCENT_COLOR}`
                    : "1px solid transparent",
                  padding: "0 8px",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }),
              }}
              onClick={() => {
                setEditingAccountUserId(row.user_id);
                fetchAccountOptions();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setEditingAccountUserId(row.user_id);
                  fetchAccountOptions();
                }
              }}
            >
              {asText(selected?.account_name || selected?.account_id || "-")}
            </span>
          </div>
        );
      }

      return (
        <div style={controlCellWrapStyle}>
          <Autocomplete
            size="small"
            disableClearable
            openOnFocus
            loading={accountOptionsLoading}
            onOpen={fetchAccountOptions}
            onClose={() => {
              setEditingAccountUserId("");
            }}
            options={accountOptions}
            value={selected}
            onChange={(_, next) => {
              handleAccountChange(row.user_id, next);
              setEditingAccountUserId("");
            }}
            autoHighlight
            blurOnSelect
            getOptionLabel={(option) => asText(option?.account_name)}
            isOptionEqualToValue={(option, value) =>
              asText(option?.account_id) === asText(value?.account_id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="거래처 검색"
                size="small"
                autoFocus
              />
            )}
            sx={{
              width: "100%",
              margin: 0,
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "& .MuiInputBase-root": {
                minHeight: CELL_INNER_HEIGHT,
                height: CELL_INNER_HEIGHT,
                backgroundColor: "#f3f5f7",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                py: "0 !important",
                px: "8px !important",
                margin: 0,
              },
              "& .MuiInputBase-root.MuiOutlinedInput-root": {
                minHeight: `${CELL_INNER_HEIGHT}px !important`,
                height: `${CELL_INNER_HEIGHT}px !important`,
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
                alignItems: "center !important",
              },
              "& .MuiInputBase-input": {
                fontSize: TABLE_FONT_SIZE,
                fontFamily: TABLE_FONT_FAMILY,
                lineHeight: `${CELL_INNER_HEIGHT}px !important`,
                height: `${CELL_INNER_HEIGHT}px !important`,
                py: "0 !important",
                px: "0 !important",
                textAlign: "center",
                boxSizing: "border-box",
                margin: "0 !important",
              },
              "& .MuiAutocomplete-inputRoot": {
                alignItems: "center !important",
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
                minHeight: `${CELL_INNER_HEIGHT}px !important`,
                height: `${CELL_INNER_HEIGHT}px !important`,
              },
              "& .MuiAutocomplete-input": {
                lineHeight: `${CELL_INNER_HEIGHT}px !important`,
                height: `${CELL_INNER_HEIGHT}px !important`,
                paddingTop: "0 !important",
                paddingBottom: "0 !important",
                paddingLeft: "0 !important",
                paddingRight: "0 !important",
                margin: "0 !important",
                display: "flex",
                alignItems: "center",
              },
              "& input::placeholder": {
                textAlign: "center",
                opacity: 1,
              },
              "& .MuiAutocomplete-popupIndicator": {
                display: "none",
              },
              "& .MuiAutocomplete-clearIndicator": {
                display: "none",
              },
              "& .MuiAutocomplete-endAdornment": {
                display: "none",
              },
              ...(deptOrAccountChanged ? changedAutocompleteSx : {}),
            }}
          />
        </div>
      );
    }

    // 5) 직책 클릭 편집 셀
    if (columnKey === "position_label") {
      const positionChanged = cellChanged;
      const rowKey = getRowKey(row);
      const positionRequiredInvalid = invalidRequiredCells[rowKey]?.includes("position");
      const positionOptions = isHeadOffice(row) ? HQ_POSITION_OPTIONS : ACCOUNT_POSITION_OPTIONS;

      if (normalizeCode(row.user_type) === "4") {
        return (
          <div style={controlCellWrapStyle}>
            <Select
              size="small"
              value={normalizeCode(row.util_member_type)}
              onChange={(e) => {
                handleUtilMemberTypeChange(row.user_id, e.target.value);
              }}
              sx={{
                ...compactSelectSx,
                ...(positionChanged ? changedSelectSx : {}),
                minWidth: 88,
                width: "100%",
              }}
            >
              {UTIL_MEMBER_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        );
      }

      if (isCeo(row)) {
        return (
          <div style={controlCellWrapStyle}>
            <Select
              size="small"
              value="대표"
              disabled
              sx={{
                ...compactSelectSx,
                ...(positionChanged ? changedSelectSx : {}),
                minWidth: 82,
                width: "100%",
              }}
            >
              <MenuItem value="대표">대표</MenuItem>
            </Select>
          </div>
        );
      }

      return (
        <div style={controlCellWrapStyle}>
          <Select
            size="small"
            value={normalizeCode(row.position)}
            onChange={(e) => {
              handlePositionChange(getRowKey(row), e.target.value);
            }}
            sx={{
              ...compactSelectSx,
              ...(positionChanged || positionRequiredInvalid ? changedSelectSx : {}),
              minWidth: 82,
              width: "100%",
            }}
          >
            {positionOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      );
    }

    // 6) 퇴사여부는 항상 드롭다운을 표시
    if (columnKey === "del_yn") {
      const userId = row.user_id;
      const currentValue = pendingDelYn[userId] ?? asText(row.del_yn || "N").toUpperCase();
      const delYnChanged = cellChanged;

      return (
        <div style={controlCellWrapStyle}>
          <Select
            key={`${userId}-${currentValue}`}
            size="small"
            value={currentValue}
            onChange={(e) => {
              handleDelYnChange(userId, e.target.value);
            }}
            sx={{
              ...compactSelectSx,
              ...(delYnChanged ? changedSelectSx : {}),
              minWidth: 76,
              width: "100%",
            }}
          >
            <MenuItem value="N">재직</MenuItem>
            <MenuItem value="Y">퇴사</MenuItem>
          </Select>
        </div>
      );
    }

    return (
      <span
        style={makeCellTextStyle({
          width: "100%",
          color: cellChanged ? CHANGED_ACCENT_COLOR : "#111",
        })}
      >
        {asText(row[columnKey]) || "-"}
      </span>
    );
  };

  const filteredRows = useMemo(() => {
    const keyword = appliedSearchKeyword.trim().toLowerCase();
    if (!keyword) return rows;

    return rows.filter((row) => asText(row.search_blob).includes(keyword));
  }, [rows, appliedSearchKeyword]);

  const totalFilteredRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredRows / PAGE_SIZE));
  const maxPageButtons = 5;

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= maxPageButtons) {
      return Array.from({ length: totalPages }, (_, idx) => idx + 1);
    }

    const half = Math.floor(maxPageButtons / 2);
    let start = currentPage - half;
    let end = currentPage + half;

    if (start < 1) {
      end += 1 - start;
      start = 1;
    }
    if (end > totalPages) {
      start -= end - totalPages;
      end = totalPages;
    }
    start = Math.max(1, start);

    return Array.from({ length: end - start + 1 }, (_, idx) => start + idx);
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearchKeyword]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const content = (
    <>
      {/* 스크롤 시 상단 네비가 화면 위에 유지되도록 고정 */}
      <MDBox
        sx={{
          position: "sticky",
          top: 0,
          zIndex: isMobileTablet ? theme.zIndex.appBar + 1 : 10,
          backgroundColor: "#ffffff",
        }}
      >
        {!embedded && <DashboardNavbar title="🧑‍🔧사용자 관리" />}
      </MDBox>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ mt: embedded ? 0 : 1 }}>
            <MDBox
              sx={{
                // 모바일에서는 검색/버튼 영역도 본문과 함께 스크롤되도록 고정 해제
                position: isMobile ? "static" : "sticky",
                top: isMobile ? "auto" : 0,
                zIndex: isMobile ? "auto" : 10,
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #eee",
              }}
            >
              <MDBox
                pt={embedded ? 0.25 : 1}
                pb={1}
                sx={{
                  display: "flex",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                  justifyContent: isMobile ? "flex-start" : "flex-end",
                  alignItems: "center",
                  gap: isMobile ? 1 : 2,
                }}
              >
                <MDBox sx={{ width: isMobile ? "100%" : "14rem", mr: isMobile ? 0 : 1 }}>
                  <MDInput
                    key={`search-input-${searchInputResetKey}`}
                    placeholder="검색(ex. 본사, 현장, 김ㅇㅇ, 한결)"
                    size="small"
                    fullWidth
                    onChange={({ currentTarget }) => {
                      searchKeywordDraftRef.current = currentTarget.value;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applySearch();
                      }
                    }}
                  />
                </MDBox>
                <MDButton
                  size="medium"
                  variant="contained"
                  color="success"
                  onClick={handleRefreshUsers}
                  disabled={loading}
                >
                  새로고침
                </MDButton>
                <MDButton
                  size="medium"
                  variant="contained"
                  color="warning"
                  onClick={handleAddHeadOfficeUser}
                  disabled={loading || saving}
                >
                  본사 사용자 등록
                </MDButton>
                <MDButton
                  size="medium"
                  variant="contained"
                  color="info"
                  onClick={handleSaveChanges}
                  disabled={saving}
                  sx={{ mr: isMobile ? 0 : 1 }}
                >
                  저장
                </MDButton>
              </MDBox>
            </MDBox>
            {/* 작은 화면에서 테이블이 박스 밖으로 넘치지 않도록 내부 스크롤 처리 */}
            <MDBox px={2} pt={1.25} pb={1} sx={{ maxHeight: "88vh", overflow: "auto" }}>
              {/* 모바일/태블릿도 데스크톱과 동일한 테이블 레이아웃으로 표시 */}
              <MDBox sx={tableSx}>
                <table>
                  <colgroup>
                    {TABLE_COLUMNS.map((col) => (
                      <col key={col.key} style={{ width: `${col.width}px` }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {TABLE_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          style={{
                            minWidth: col.width,
                            width: col.width,
                            ...getStickyColumnStyle(col.key, true),
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.map((row) => (
                      <tr key={getRowKey(row)}>
                        {TABLE_COLUMNS.map((col) => (
                          <td
                            key={`${getRowKey(row)}_${col.key}`}
                            style={{
                              minWidth: col.width,
                              width: col.width,
                              textAlign: col.align || "center",
                              verticalAlign: "middle",
                              paddingLeft: 4,
                              paddingRight: 4,
                              fontFamily: TABLE_FONT_FAMILY,
                              fontSize: `${TABLE_FONT_SIZE}px`,
                              ...getStickyColumnStyle(col.key, false),
                            }}
                          >
                            {renderTableCellContent(row, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {pagedRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={TABLE_COLUMNS.length}
                          style={{
                            textAlign: "center",
                            padding: "10px",
                            fontFamily: TABLE_FONT_FAMILY,
                            fontSize: `${TABLE_FONT_SIZE}px`,
                          }}
                        >
                          데이터가 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </MDBox>
            </MDBox>
            <MDBox
              px={2}
              py={1}
              sx={{
                borderTop: "1px solid #eceff3",
                display: "flex",
                flexWrap: isMobile ? "wrap" : "nowrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <MDBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "32px 1fr 32px",
                    alignItems: "center",
                    gap: 0.5,
                    width: isMobile ? "100%" : 220,
                  }}
                >
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="dark"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    sx={{ minWidth: 32, px: 0 }}
                  >
                    {"<"}
                  </MDButton>
                  <MDBox sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                    {visiblePageNumbers.map((pageNum) => (
                      <MDButton
                        key={pageNum}
                        size="small"
                        variant={pageNum === currentPage ? "contained" : "outlined"}
                        color={pageNum === currentPage ? "info" : "dark"}
                        onClick={() => setCurrentPage(pageNum)}
                        sx={{ minWidth: 32, px: 0 }}
                      >
                        {pageNum}
                      </MDButton>
                    ))}
                  </MDBox>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="dark"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    sx={{ minWidth: 32, px: 0 }}
                  >
                    {">"}
                  </MDButton>
                </MDBox>
              </MDBox>
              <MDTypography variant="caption" color="text">
                총 {totalFilteredRows}건 - {currentPage}/{totalPages}페이지
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  return embedded ? content : <DashboardLayout>{content}</DashboardLayout>;
}

UserManagement.propTypes = {
  embedded: PropTypes.bool,
};

export default UserManagement;
